import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { AlertTriangle, ShoppingBasket, Trash2 } from "lucide-react";
import { Reveal } from "@/components/Reveal";
import { useBasket } from "@/lib/BasketContext";
import { api, gbp } from "@/lib/api";

export default function Basket() {
  const { items, setQty, remove } = useBasket();
  const navigate = useNavigate();
  const [validation, setValidation] = useState(null);
  const [checking, setChecking] = useState(false);

  useEffect(() => {
    if (!items.length) {
      setValidation(null);
      return;
    }
    setChecking(true);
    const timer = setTimeout(() => {
      api
        .post("/public/checkout/quote", { items, fulfilment: "collection" })
        .then((res) => setValidation(res.data))
        .catch(() => setValidation(null))
        .finally(() => setChecking(false));
    }, 350);
    return () => clearTimeout(timer);
  }, [items]);

  const issues = validation?.issues || [];
  const allAllergens = [...new Set((validation?.items || []).flatMap((i) => i.allergens || []))];
  const allMayContain = [...new Set((validation?.items || []).flatMap((i) => i.may_contain || []))];

  return (
    <div className="mx-auto max-w-3xl px-4 py-12 sm:px-6 sm:py-16 lg:px-8" data-testid="basket-page">
      <Reveal>
        <h1 className="font-serif text-3xl font-bold tracking-tight text-cream-text sm:text-4xl">Your Basket</h1>
      </Reveal>

      {items.length === 0 ? (
        <Reveal className="mt-10">
          <div className="flex flex-col items-center rounded-3xl border border-choco-line bg-choco-light px-6 py-16 text-center" data-testid="basket-empty-state">
            <ShoppingBasket className="h-12 w-12 text-gold" aria-hidden="true" />
            <h2 className="mt-5 font-serif text-2xl font-semibold text-cream-text">Your basket is empty</h2>
            <p className="mt-2 max-w-sm text-sm text-cream-muted">Browse this week's bakes and fill it with something delicious.</p>
            <Link to="/shop" className="mt-7 inline-flex items-center gap-2 rounded-full bg-gold px-7 py-3 text-sm font-bold text-choco transition-colors hover:bg-gold-hover focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-gold-focus active:scale-[0.98]" data-testid="basket-shop-link">
              Shop This Week's Bakes
            </Link>
          </div>
        </Reveal>
      ) : (
        <Reveal className="mt-10">
          {issues.length > 0 && (
            <div className="mb-6 space-y-2 rounded-3xl border border-amber-500/50 bg-amber-500/10 p-5" data-testid="basket-issues">
              {issues.map((issue, i) => (
                <p key={i} className="flex items-start gap-2 text-sm text-amber-200" data-testid={`basket-issue-${i}`}>
                  <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" aria-hidden="true" /> {issue.message}
                </p>
              ))}
            </div>
          )}

          <ul className="space-y-4" data-testid="basket-items-list">
            {(validation ? validation.items : items).map((item, i) => (
              <li key={`${item.product_id}-${item.variant_id || "base"}`} className="flex items-center gap-4 rounded-3xl bg-cream p-4 text-cream-darktext sm:p-5" data-testid={`basket-item-${i}`}>
                {item.image && <img src={item.image} alt="" className="h-16 w-16 shrink-0 rounded-2xl object-cover" />}
                <div className="min-w-0 flex-1">
                  <p className="font-serif text-lg font-semibold">{item.name}</p>
                  {item.option_values && Object.keys(item.option_values).length > 0 && (
                    <p className="mt-0.5 text-xs text-cream-darkmuted">
                      {Object.entries(item.option_values).map(([k, v]) => `${k}: ${v}`).join(" · ")}
                    </p>
                  )}
                  <p className="mt-1 text-sm font-bold text-gold-hover">{gbp(item.unit_price_pence)}</p>
                </div>
                <label className="sr-only" htmlFor={`basket-qty-${i}`}>Quantity</label>
                <input
                  id={`basket-qty-${i}`}
                  type="number"
                  min="0"
                  value={item.qty}
                  onChange={(e) => setQty(i, parseInt(e.target.value || "0", 10))}
                  className="w-16 rounded-xl border border-cream-darkmuted/30 bg-white px-2 py-1.5 text-center text-sm text-cream-darktext focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-gold-focus"
                  data-testid={`basket-qty-input-${i}`}
                />
                <button
                  onClick={() => remove(i)}
                  aria-label={`Remove ${item.name}`}
                  className="rounded-full p-2 text-cream-darkmuted transition-colors hover:text-red-600 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-gold-focus"
                  data-testid={`basket-remove-${i}`}
                >
                  <Trash2 className="h-4 w-4" aria-hidden="true" />
                </button>
              </li>
            ))}
          </ul>

          {(allAllergens.length > 0 || allMayContain.length > 0) && (
            <div className="mt-6 rounded-3xl border border-gold/40 bg-choco-light p-5" data-testid="basket-allergen-summary">
              <h2 className="flex items-center gap-2 text-sm font-bold text-cream-text">
                <AlertTriangle className="h-4 w-4 text-gold" aria-hidden="true" /> Allergens in your basket
              </h2>
              {allAllergens.length > 0 && (
                <p className="mt-1 text-sm capitalize text-cream-muted">Contains: {allAllergens.join(", ")}</p>
              )}
              {allMayContain.length > 0 && (
                <p className="mt-1 text-sm capitalize text-amber-300" data-testid="basket-may-contain">May contain: {allMayContain.join(", ")}</p>
              )}
              <p className="mt-2 text-xs text-cream-muted">
                All bakes are made in a kitchen that handles common allergens. If you have an allergy, please{" "}
                <Link to="/contact" className="font-bold text-gold hover:underline">contact us</Link> before ordering.
              </p>
            </div>
          )}

          <div className="mt-6 flex items-center justify-between rounded-3xl border border-choco-line bg-choco-light p-5" data-testid="basket-summary">
            <span className="text-sm font-semibold text-cream-text">Subtotal {checking && <span className="text-xs text-cream-muted">(checking…)</span>}</span>
            <span className="font-serif text-xl font-bold text-cream-text" data-testid="basket-subtotal">
              {gbp(validation ? validation.subtotal_pence : items.reduce((n, i) => n + i.qty * i.unit_price_pence, 0))}
            </span>
          </div>
          <p className="mt-2 text-xs text-cream-muted">Prices are confirmed live with the bakery before checkout.</p>

          <button
            onClick={() => navigate("/checkout")}
            className="mt-6 w-full rounded-full bg-gold px-8 py-3.5 text-base font-bold text-choco transition-colors duration-150 hover:bg-gold-hover focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-gold-focus focus-visible:ring-offset-2 focus-visible:ring-offset-choco active:scale-[0.98]"
            data-testid="proceed-to-checkout-button"
          >
            Proceed to Checkout
          </button>
        </Reveal>
      )}
    </div>
  );
}
